/**
 * A long list, chosen from a dialog -- the form engine's LongSelect.
 *
 * ED, 2026-10-28, ON THE PURCHASE ORDERS PICKER: "Does the list scroll? Maybe we need a more complex
 * picker such as what is implemented as a Long Picker." It is called LongSelect, and the source
 * product's milestone-reference form shows it in use: a read-only box with a list icon beside it,
 * which opens a dialog holding a GRID -- columns, search, sort -- and a Select button
 * (PlatformLongSelect, over an enterprise component and pick maps). A form-engine field has no
 * component to query; its options arrive resolved, each with a Value, a Display and -- when the
 * resolver lists `columns` -- the attributes to draw as columns. This is the same control over that
 * shape: box and icon, dialog, search across every column, sortable headers, checkboxes for a Many
 * field or a radio for a One, Select and Cancel. The dropdown stays for short lists.
 *
 * WHAT IT NEVER DOES: invent or drop a value. A value the picker would no longer offer stays listed
 * and disabled, as the dropdown shows it, so it can be read and removed but not chosen again.
 */
import React, {useMemo, useState} from "react";
import {Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Radio,
        Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, TextField, Typography} from "@mui/material";
import {ListAlt} from "@mui/icons-material";

import {Grey6, Grey7, Grey17} from "../../theme/styles";

export type LongOption = {
    Value: string; Display: string | null; Offered?: boolean; Orphan?: boolean;
    /** Column values, in the resolver's column order, when the resolver lists `columns`. */
    Attributes?: Record<string, string | null>;
};

// A dropdown past this many options is a list to search, not a list to scroll.
export const LONG_LIST = 12;

const UNNAMED = "(unnamed)";

// Column headings from attribute names: "ObjectID" -> "ID", "RevisionStatus" -> "Status",
// "ObjectTypeName" -> "Type". Anything else splits its CamelCase.
const HEADINGS: Record<string, string> = {
    ObjectID: "ID", RevisionStatus: "Status", RevisionName: "Name", ObjectTypeName: "Type",
    ObjectTypeAcronym: "Type", RevisionDisplay: "Rev", Email: "Email", Name: "Name",
};
const headingOf = (name: string) => HEADINGS[name] ?? name.replace(/([a-z])([A-Z])/g, "$1 $2");

export function FormLongSelect({label, options, many, value, onChange, disabled}: {
    label: string;
    options: LongOption[];
    many: boolean;
    value: any;
    onChange: (next: any) => void;
    disabled?: boolean;
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState<{column: string | null; ascending: boolean}>({column: null, ascending: true});
    const chosen: string[] = many
        ? (Array.isArray(value) ? value.map(String) : [])
        : (value === null || value === undefined || value === "" ? [] : [String(value)]);
    const [draft, setDraft] = useState<string[]>(chosen);

    // THE GRID'S COLUMNS come from the options themselves -- the first option's Attributes, in the
    // order the resolver listed them. Options with no Attributes draw one column, the Display.
    const columns: string[] = useMemo(() => {
        const first = options.find(one => one.Attributes && Object.keys(one.Attributes).length);
        return first ? Object.keys(first.Attributes!) : [];
    }, [options]);

    const byValue = useMemo(() => {
        const map: Record<string, LongOption> = {};
        options.forEach(one => { map[one.Value] = one; });
        return map;
    }, [options]);

    const cell = (one: LongOption, column: string) => one.Attributes?.[column] ?? "";

    const shown = useMemo(() => {
        const needle = search.trim().toLowerCase();
        let rows = !needle ? options : options.filter(one =>
            (one.Display ?? "").toLowerCase().includes(needle)
            || one.Value.toLowerCase().includes(needle)
            || columns.some(column => (cell(one, column) ?? "").toLowerCase().includes(needle)));
        if (sort.column) {
            const key = (one: LongOption) => (sort.column === "__display"
                ? (one.Display ?? "") : (cell(one, sort.column!) ?? "")).toLowerCase();
            rows = [...rows].sort((a, b) => key(a).localeCompare(key(b)) * (sort.ascending ? 1 : -1));
        }
        return rows;
    }, [options, search, sort, columns]);

    const begin = () => { setDraft(chosen); setSearch(""); setOpen(true); };
    const toggle = (one: LongOption) => {
        if (one.Offered === false && !draft.includes(one.Value)) return;
        setDraft(previous => many
            ? (previous.includes(one.Value) ? previous.filter(v => v !== one.Value) : [...previous, one.Value])
            : [one.Value]);
    };
    const select = () => {
        onChange(many ? draft : (draft[0] ?? null));
        setOpen(false);
    };
    const sortBy = (column: string) => setSort(previous =>
        previous.column === column ? {column, ascending: !previous.ascending} : {column, ascending: true});

    const summary = chosen.length === 0
        ? null
        : chosen.map(v => byValue[v]?.Display ?? UNNAMED).join(", ");

    const headers = columns.length ? columns : ["__display"];

    return (
        <>
            {/* THE FIELD AS IT READS ON THE FORM: what is chosen, and the list icon that opens the
                chooser -- the legacy detail page's idiom, so the control is recognisable. */}
            <Box sx={{display: "flex", alignItems: "center", gap: "6px"}}>
                <TextField size="small" fullWidth value={summary ?? ""} placeholder="Select"
                           onClick={() => !disabled && begin()}
                           disabled={disabled}
                           slotProps={{input: {readOnly: true, style: {cursor: disabled ? "default" : "pointer"}},
                                       htmlInput: {style: {cursor: disabled ? "default" : "pointer"}}}}/>
                <IconButton size="small" disabled={disabled} onClick={begin} aria-label={`Choose ${label}`}>
                    <ListAlt sx={{fontSize: 22}}/>
                </IconButton>
            </Box>
            {open && (
                <Dialog open onClose={() => setOpen(false)} fullWidth maxWidth="md">
                    <DialogTitle sx={{fontSize: 16}}>{label}</DialogTitle>
                    <DialogContent sx={{display: "flex", flexDirection: "column", gap: "10px"}}>
                        <TextField size="small" fullWidth autoFocus value={search}
                                   onChange={e => setSearch(e.target.value)}
                                   placeholder={`Search ${options.length} options`}/>
                        <Typography sx={{fontSize: 12, color: Grey7}}>
                            {many ? `${draft.length} selected` : (draft.length ? "1 selected" : "Nothing selected")}
                            {search ? ` · ${shown.length} of ${options.length} shown` : ""}
                        </Typography>
                        <Box sx={{maxHeight: "55vh", overflowY: "auto", border: "1px solid #E4E7EC", borderRadius: "4px"}}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox"/>
                                        {headers.map(column => (
                                            <TableCell key={column} sx={{fontSize: 11.5, color: Grey7,
                                                                          textTransform: "uppercase", letterSpacing: "0.04em"}}>
                                                <TableSortLabel active={sort.column === column}
                                                                direction={sort.column === column && !sort.ascending ? "desc" : "asc"}
                                                                onClick={() => sortBy(column)}>
                                                    {column === "__display" ? label : headingOf(column)}
                                                </TableSortLabel>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {shown.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={headers.length + 1}>
                                                <Typography sx={{fontSize: 13, color: Grey6}}>Nothing matches.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {shown.map(one => {
                                        const picked = draft.includes(one.Value);
                                        const gone = one.Offered === false;
                                        return (
                                            <TableRow key={one.Value} hover selected={picked}
                                                      onClick={() => toggle(one)}
                                                      sx={{cursor: gone && !picked ? "default" : "pointer",
                                                           opacity: gone && !picked ? 0.5 : 1}}>
                                                <TableCell padding="checkbox">
                                                    {many ? <Checkbox size="small" checked={picked} tabIndex={-1} disabled={gone && !picked}/>
                                                          : <Radio size="small" checked={picked} tabIndex={-1} disabled={gone && !picked}/>}
                                                </TableCell>
                                                {headers.map(column => (
                                                    <TableCell key={column} sx={{fontSize: 13, color: Grey17}}>
                                                        {column === "__display" ? (one.Display ?? UNNAMED) : cell(one, column)}
                                                        {column === headers[0] && gone ? (
                                                            <span style={{color: Grey6, marginLeft: 6, fontSize: 12}}>
                                                                · {one.Orphan ? "no longer exists" : "no longer offered"}
                                                            </span>
                                                        ) : null}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button size="small" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button size="small" variant="contained" onClick={select}>Select</Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
}
